/**
 * What else is running on this page, and what it has already broken.
 *
 * ## Why an analytics script reports on its own neighbours
 *
 * Every support conversation about a tracking script is the same conversation:
 * "my numbers are wrong". Almost none of them are about the tracking script.
 * They are about a consent manager that holds the tag until a click that never
 * comes, a tag manager that injected a second copy so every page counts twice,
 * a router shim that replaced `history.pushState` and does not call through, an
 * A/B testing tool that reloads the page before anything is sent.
 *
 * None of that is visible from the server, which sees only that the numbers are
 * low, or high, or lumpy. It is not visible in the customer's dashboard. It is
 * barely visible to the customer, who compares two tools and concludes that one
 * of them is broken. It IS visible from inside the page — to us, for free, at
 * the moment we load — and sending it turns a week of guessing into a panel
 * that says "you have two copies of this tag" or "something wrapped
 * `pushState` before we did".
 *
 * ## Why it is not a privacy problem, stated plainly rather than assumed
 *
 * This describes the CUSTOMER'S OWN PAGE, not their visitor. It is the same
 * list the customer gets from "view source" on their own site, and the same
 * list any of their visitors could read. It contains no identifier, nothing
 * derived from the visitor's device or behaviour, and nothing that varies
 * between two people loading the same page.
 *
 * Hosts only, and never the script URLs: a URL carries a path and a query, and
 * a third-party script URL with an account id or a session token in it is
 * completely ordinary. The host answers the question; the rest is somebody
 * else's business.
 */
import { MAX_SCRIPT_HOSTS } from '@panel/shared/tracker';

/**
 * Was `history.pushState` already wrapped when we arrived?
 *
 * A native method stringifies with `[native code]` in its body, in every engine
 * that has ever shipped. Anything else is a function somebody installed, which
 * means two wrappers on the same method — the standard way a single-page app's
 * view count goes wrong, and the only one of these diagnostics that must be
 * measured BEFORE we do our own patching, since a moment later the answer would
 * be "yes, by us".
 *
 * A false positive is possible and cheap: a polyfill or a devtools hook also
 * fails this test and is harmless. Reported as a fact ("something wrapped this
 * first"), never as a verdict.
 */
export function historyAlreadyWrapped(win: Window): boolean {
  try {
    const push = win.history && win.history.pushState;
    if (typeof push !== 'function') return false;
    return String(push).indexOf('[native code]') === -1;
  } catch {
    return false;
  }
}

/**
 * The hosts of third-party scripts sharing the page, deduplicated and capped.
 *
 * First-party scripts are excluded: a customer knows what they put on their own
 * origin, and including them would make the list mostly noise and mostly bytes.
 * Inline scripts have no host and cannot be attributed to anyone.
 *
 * ## What this misses, on purpose
 *
 * It is a snapshot taken when the first page view is sent, which is a few
 * milliseconds after the document parsed. A script that a tag manager injects
 * two seconds later is not in it. Waiting for the page to settle would delay
 * the page view itself, and a page view that arrives late is a page view lost
 * on every visitor who bounces — the wrong side of that trade for a diagnostic.
 * The list is therefore "what was in the document", which is also the list the
 * customer can check against their own template.
 */
export function thirdPartyScriptHosts(doc: Document, loc: Location): string[] {
  const hosts: string[] = [];
  try {
    const tags = doc.getElementsByTagName('script');
    const own = loc.hostname;
    for (let i = 0; i < tags.length && hosts.length < MAX_SCRIPT_HOSTS; i++) {
      const src = tags[i] ? (tags[i] as HTMLScriptElement).src : '';
      if (!src) continue;
      let host = '';
      try {
        host = new URL(src, loc.href).hostname;
      } catch {
        continue;
      }
      if (!host || host === own) continue;
      if (hosts.indexOf(host) === -1) hosts.push(host);
    }
  } catch {
    // A DOM that will not enumerate is not worth an exception in a customer's
    // page over a diagnostic. An empty list reads as "nothing to report", which
    // is the safe direction: the dashboard says nothing rather than something
    // wrong.
    return [];
  }
  return hosts;
}
